import { useRef } from 'react';

import ActionButton, { type ActionButtonState } from 'components/ActionButton';
import type { HTMLAttributes } from 'types';

import InputInfo from './InputInfo';

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
	disabled?: boolean;
	errorMessage?: string | string[];
	helpText?: string;
	id: string;
	label: string;
	onChange: (file: File) => void;
	uploadState: ActionButtonState;
}

export default function File({
	disabled,
	errorMessage,
	helpText,
	id,
	label,
	onChange,
	uploadState,
	...attrs
}: Props) {
	const ref = useRef<HTMLInputElement>(null);
	const errorMessageId = `${id}-error`;
	const helpTextId = `${id}-help`;

	return (
		<div {...attrs}>
			<input
				id={id}
				type="file"
				className="hidden"
				ref={ref}
				disabled={disabled}
				onChange={(event) => {
					const file = event.target?.files?.[0];
					if (!file) {
						return;
					}
					onChange(file);
				}}
			/>
			<ActionButton
				// Since the input is hidden we associate the help and error messages
				// with the button instead
				aria-describedby={[errorMessageId, helpTextId].join(' ')}
				displayWidth="full"
				onClick={() => {
					ref.current?.click();
				}}
				customState={uploadState}
				variant="branded"
				disabled={disabled}
			>
				{label}
			</ActionButton>

			<InputInfo
				className="ml-0"
				errorMessage={errorMessage}
				invalid={Boolean(errorMessage)}
				errorMessageId={errorMessageId}
				helpText={helpText}
				helpTextId={helpTextId}
			/>
		</div>
	);
}
File.displayName = 'FormUi_File';
